import React from 'react';
import { useLabSessionHealth } from '../../hooks/lab-hooks';
import { labHealthSentiment } from '../../support/labService';
import { LabDate, LabJsonView, LabPanelState, LabStatusTag, labDuration } from './LabShared';

export type LabDeviceHealthPanelProps = {
  sessionKey: string;
  /** Hide devices the scenario does not monitor. On by default; they report nothing. */
  monitoredOnly?: boolean;
};

/**
 * What the pod's own agent last said about its devices.
 *
 * `stale` and `state_duration_seconds` are computed by the backend rather than here, so this
 * view and the fleet sweep cannot disagree about whether a device has gone quiet. A stale
 * "up" is the dangerous reading: the device was fine the last time anybody heard, and nobody
 * has heard since.
 */
const LabDeviceHealthPanel: React.FC<LabDeviceHealthPanelProps> = ({
  sessionKey,
  monitoredOnly = true,
}) => {
  const { data, isLoading, error } = useLabSessionHealth(sessionKey);

  const devices = (data?.devices || []).filter((device) => !monitoredOnly || device.monitored);

  return (
    <LabPanelState
      loading={isLoading}
      error={error}
      empty={!devices.length}
      emptyMessage="No device health has been reported for this session yet. The agent reports on its poll interval."
      loadingLabel="Loading device health..."
    >
      <div className="overflow-x-auto">
        <table className="w-full text-left text-sm">
          <thead>
            <tr className="border-b border-gray-200 text-xs uppercase tracking-wide text-gray-500">
              <th className="px-3 py-2 font-semibold">Device</th>
              <th className="px-3 py-2 font-semibold">State</th>
              <th className="px-3 py-2 font-semibold">In this state</th>
              <th className="px-3 py-2 font-semibold">Last report</th>
              <th className="px-3 py-2 font-semibold">Last OK</th>
            </tr>
          </thead>

          <tbody>
            {devices.map((device) => (
              <tr key={device.id} className="border-b border-gray-100 align-top">
                <td className="px-3 py-2">
                  <div className="font-medium">{device.device_key}</div>
                  <div className="text-xs text-gray-500">{device.display_name}</div>
                  {device.address && (
                    <div className="font-mono text-xs text-gray-500">{device.address}</div>
                  )}
                  {device.last_error && (
                    <div className="mt-1 text-xs text-red-700">{device.last_error}</div>
                  )}
                  {device.metrics && (
                    <details className="mt-1">
                      <summary className="cursor-pointer text-xs text-gray-500">Metrics</summary>
                      <LabJsonView value={device.metrics} />
                    </details>
                  )}
                </td>

                <td className="px-3 py-2 whitespace-nowrap">
                  <LabStatusTag
                    label={device.state}
                    sentiment={labHealthSentiment(device.state)}
                  />

                  {/* A stale reading is not a reading. Say so next to it. */}
                  {device.stale && (
                    <div className="mt-1">
                      <LabStatusTag label="Stale" sentiment="warning" />
                    </div>
                  )}

                  {device.flap_count > 2 && (
                    <div className="text-xs text-amber-800">
                      {`flapped ${device.flap_count} times`}
                    </div>
                  )}
                </td>

                <td className="px-3 py-2 whitespace-nowrap">
                  {labDuration(device.state_duration_seconds)}
                </td>

                <td className="px-3 py-2 whitespace-nowrap">
                  <LabDate value={device.last_report_at} />
                </td>

                <td className="px-3 py-2 whitespace-nowrap">
                  <LabDate value={device.last_ok_at} fallback="never" />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </LabPanelState>
  );
};

export default LabDeviceHealthPanel;
